import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import cx from 'classnames'
import { useForm, Controller } from 'react-hook-form'
import { StylesProvider } from '@material-ui/core/styles'
import {
  Button,
  Typography,
  FormControlLabel,
  FormControl,
  Tooltip,
  MenuItem,
  Select
} from '@material-ui/core'
import styles from './DownloadSettingsForm.module.scss'
import moment from 'moment'
import { ArrowBack, ArrowForward } from '@material-ui/icons'
import { LoadingButton } from '@mui/lab'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { dataGroupingDefault, dataGroupingValues } from 'constants/DataGrouping'
import { AppDatePicker, toDateInputValue } from 'common/components/DatePicker'

const DownloadSettingsForm = props => {
  const { onSubmit, translate, system, sortedData, isPremium, submitError } = props
  let { handleSubmit, register, errors, control, getValues, setError, clearErrors, formState } =
    useForm()
  const { isSubmitting } = formState

  const [type, setType] = useState([])
  const [value, setValue] = useState('csv')
  const [orderValue, setOrderValue] = useState('0')
  const [groupingValue, setGroupingValue] = useState('c')
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [typeChecked, setTypeChecked] = useState([])
  const [checkAllIsClicked, setCheckAllIsClicked] = useState(false)
  const selectAllCheckboxRef = useRef(null)

  const selectedTypes = type.filter(dataType => typeChecked.includes(dataType))

  const groupingItemBuilder = ({ value, translation }) => {
    return (
      <MenuItem
        value={value}
        className={styles.menuItem}
        classes={{
          selected: styles.menuItemSelected
        }}
      >
        {translate(translation)}
      </MenuItem>
    )
  }

  const handleChangeOrder = event => {
    setOrderValue(event.target.value)
  }

  const handleChangeFileExtension = event => {
    setValue(event.target.value)
  }

  const handleChangeGrouping = event => {
    setGroupingValue(event.target.value)
  }

  const getGroupingLabel = selectedValue => {
    const groupingOptions = [dataGroupingDefault, ...dataGroupingValues]
    const groupingOption = groupingOptions.find(item => item.value === selectedValue)

    return groupingOption ? translate(groupingOption.translation) : selectedValue
  }

  const getOrderLabel = selectedValue => {
    if (selectedValue === '0') return translate('downloadDataForm.olderDataFirst')
    if (selectedValue === '1') return translate('downloadDataForm.latestDataFirst')
    return selectedValue
  }

  const getFileExtensionLabel = selectedValue => translate(selectedValue)

  const handleChange = event => {
    if (event.target.type === 'radio') {
      const item = event.target.value
      setValue(item)
    }
    if (event.target.type === 'checkbox') {
      const { value, checked } = event.target
      setTypeChecked([...typeChecked, value])
      if (!checked) {
        setTypeChecked(typeChecked.filter(item => item !== value))
      }
      let numberOfParams = 0
      type.map((t, i) => {
        if (getValues(`types[${i}]`) && getValues(`types[${i}]`).length > 0) {
          // Hay seleccionados
          numberOfParams++
        }
        return null
      })
      if (numberOfParams === 0) {
        setError('types', { shouldFocus: 'types' })
      } else {
        clearErrors('types')
      }
    }
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const handleSelectAll = event => {
    setIsCheckAll(!isCheckAll)
    setTypeChecked(type)
    setCheckAllIsClicked(true)
    if (isCheckAll) {
      setTypeChecked([])
    } else {
      clearErrors('types')
    }
  }

  const checkInputs = () => {
    if (moment(getValues('endDate')) < moment(getValues('startDate'))) return 'Invalid date'

    let data = {}
    data.weather = selectedTypes.includes('weather')
    data.params = selectedTypes.filter(dataType => dataType !== 'weather')
    data.endDate = getValues('endDate')
    data.startDate = getValues('startDate')
    data.fileType = value
    data.order = orderValue
    data.interval = groupingValue

    return data
  }

  useEffect(() => {
    let types = []
    sortedData.map(d => {
      types.push(d.type)
      return null
    })
    if (system.weather && system.weather.baseStationLocation) types.push('weather')
    setType(types)
  }, [sortedData, system])

  const isIndeterminate = () => {
    return checkAllIsClicked && typeChecked.length > 0 && typeChecked.length !== type.length
  }

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate()
    }
  }, [checkAllIsClicked, typeChecked.length, type.length])

  const onClickNext = () => {
    if (activeTab === 0) {
      let numberOfParams = 0
      numberOfParams = selectedTypes.length
      if (numberOfParams !== 0) {
        setActiveTab(1)
      } else {
        setError('types', { shouldFocus: 'types' })
      }
    }
  }

  const onClickBack = () => {
    setActiveTab(0)
  }

  const onClickDownload = () => {
    const data = checkInputs()
    if (data === 'Invalid date') return
    return onSubmit(data)
  }

  const getDownloadMinDate = () => {
    if (isPremium) return undefined

    const date = new Date()
    date.setDate(date.getDate() - 30)

    return toDateInputValue(date)
  }

  return (
    <StylesProvider injectFirst>
      <div className={styles.modalContainer}>
        <Typography className={styles.header}>{translate('downloadData')}</Typography>
        <div className={styles.modal}>
          <form
            noValidate
            onSubmit={handleSubmit(onClickDownload)}
          >
            <div style={{ display: activeTab === 0 ? 'block' : 'none' }}>
              <div className={styles.firstPage}>
                <div className={styles.nav}>
                  <Typography className={styles.subheaderParamsSelected}>
                    {translate('editNavParams')}
                  </Typography>
                  <Typography className={styles.subheaderDateandFormat}>
                    {translate('navDateAndFormat')}
                  </Typography>
                </div>
                <div className={styles.subInfoWrapper}>
                  <Typography className={styles.subInfo}>
                    {translate('downloadDataForm.selectParameters')}
                  </Typography>
                </div>
                <FormControlLabel
                  className={styles.selectAllCheckboxForm}
                  control={
                    <input
                      checked={type.length > 0 && typeChecked.length === type.length}
                      className={styles.checkbox}
                      name='selectAll'
                      id='selectAll'
                      onChange={handleSelectAll}
                      ref={selectAllCheckboxRef}
                      type='checkbox'
                    />
                  }
                  label={translate('downloadDataForm.selectAll')}
                />
                <div className={styles.checkboxWrapper}>
                  {type.map((dataType, i) => {
                    return (
                      <div
                        className={styles.checkboxFormDiv}
                        key={i}
                      >
                        <FormControlLabel
                          className={styles.checkboxForm}
                          control={
                            <input
                              checked={typeChecked.includes(dataType)}
                              className={styles.checkbox}
                              onChange={handleChange}
                              required
                              name={`types[${i}]`}
                              value={dataType}
                              ref={register()}
                              type='checkbox'
                            />
                          }
                          label={translate(dataType)}
                        />
                      </div>
                    )
                  })}
                </div>
                {errors.types && (
                  <div className={styles.error}>
                    <small>{translate('validation.anyFieldRequired')}</small>
                  </div>
                )}
                <div className={styles.buttonWrapper1}>
                  <Button
                    fullWidth
                    variant='contained'
                    className={styles.buttonSiguiente}
                    onClick={onClickNext}
                  >
                    {translate('next')}
                    <ArrowForward className={styles.arrow} />
                  </Button>
                </div>
                <div className={styles.buttonWrapper}>
                  <Button
                    fullWidth
                    variant='contained'
                    className={styles.buttonCancelar}
                    onClick={onClickCancel}
                  >
                    {translate('cancel')}
                  </Button>
                </div>
              </div>
            </div>
            <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
              <div className={styles.secondPage}>
                <div className={styles.nav}>
                  <Typography className={styles.subheaderParams}>
                    {translate('editNavParams')}
                  </Typography>
                  <Typography className={styles.subheaderDateandFormatSelected}>
                    {translate('navDateAndFormat')}
                  </Typography>
                </div>
                <div className={styles.dataGroupingWrapper}>
                  <Typography className={styles.selectLabel}>
                    {translate('downloadDataForm.dataGrouping')}
                  </Typography>
                  <FormControl>
                    <Select
                      id='dataGrouping'
                      value={groupingValue}
                      displayEmpty
                      renderValue={getGroupingLabel}
                      onChange={handleChangeGrouping}
                      variant='standard'
                      disableUnderline
                      className={styles.select}
                      IconComponent={KeyboardArrowDownIcon}
                      classes={{
                        icon: styles.selectIcon,
                        select: styles.selectFocus
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        getContentAnchorEl: null,
                        MenuListProps: {
                          className: styles.selectMenuList
                        },
                        PaperProps: {
                          className: styles.selectMenuPaper
                        }
                      }}
                    >
                      {groupingItemBuilder(dataGroupingDefault)}
                      {dataGroupingValues.map(item => {
                        if (system.timeInterval <= item.minutes) {
                          return groupingItemBuilder(item)
                        }
                      })}
                    </Select>
                  </FormControl>
                </div>
                <div>
                  <Typography className={styles.subInfo}>
                    {translate('downloadDataForm.selectDateInterval')}
                  </Typography>
                </div>
                <Tooltip
                  title={!isPremium ? translate('businessPlan') : ''}
                  placement='right'
                  arrow
                >
                  <div className={styles.getMoreRange}>
                    {translate('downloadDataForm.availableRange')}
                    <span className={styles.infoIcon}>i</span>
                  </div>
                </Tooltip>
                <div className={styles.datePickerWrapper}>
                  <div className={styles.picker}>
                    <Controller
                      control={control}
                      name='startDate'
                      defaultValue=''
                      rules={{ required: true }}
                      render={({ onChange, value }) => (
                        <AppDatePicker
                          disableFuture
                          error={Boolean(errors.startDate)}
                          inputClassName={styles.datePicker}
                          label={translate('startDate')}
                          min={getDownloadMinDate()}
                          name='startDatePicker'
                          onChange={onChange}
                          required
                          value={value}
                        />
                      )}
                    />
                  </div>
                  {errors.startDate && (
                    <div className={styles.error}>
                      <small>{translate('validation.required')}</small>
                    </div>
                  )}
                  <div className={styles.picker}>
                    <Controller
                      control={control}
                      name='endDate'
                      defaultValue=''
                      rules={{ required: true, validate: checkInputs }}
                      render={({ onChange, value }) => (
                        <AppDatePicker
                          disableFuture
                          error={Boolean(errors.endDate)}
                          inputClassName={styles.datePicker}
                          label={translate('endDate')}
                          min={getDownloadMinDate()}
                          name='endDatePicker'
                          onChange={onChange}
                          required
                          value={value}
                        />
                      )}
                    />
                  </div>
                  {errors.endDate && errors.endDate?.type === 'required' && (
                    <div className={styles.error}>
                      <small>{translate('validation.required')}</small>
                    </div>
                  )}
                  {errors.endDate && errors.endDate?.type === 'validate' && (
                    <div className={styles.error}>
                      <small>{translate('validation.invalidDateRange')}</small>
                    </div>
                  )}
                </div>
                <div className={styles.fileExtensionWrapper}>
                  <Typography className={styles.selectLabel}>
                    {translate('downloadDataForm.downloadOrder')}
                  </Typography>
                  <FormControl>
                    <Select
                      id='orderSelect'
                      value={orderValue}
                      displayEmpty
                      renderValue={getOrderLabel}
                      onChange={handleChangeOrder}
                      variant='standard'
                      disableUnderline
                      className={styles.select}
                      IconComponent={KeyboardArrowDownIcon}
                      classes={{
                        icon: styles.selectIcon,
                        select: styles.selectFocus
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        getContentAnchorEl: null,
                        MenuListProps: {
                          className: styles.selectMenuList
                        },
                        PaperProps: {
                          className: styles.selectMenuPaper
                        }
                      }}
                    >
                      <MenuItem
                        value='0'
                        className={styles.menuItem}
                        classes={{
                          selected: styles.menuItemSelected
                        }}
                      >
                        {translate('downloadDataForm.olderDataFirst')}
                      </MenuItem>
                      <MenuItem
                        value='1'
                        className={styles.menuItem}
                        classes={{
                          selected: styles.menuItemSelected
                        }}
                      >
                        {translate('downloadDataForm.latestDataFirst')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className={styles.fileExtensionWrapper}>
                  <Typography className={styles.selectLabel}>
                    {translate('downloadDataForm.downloadExtension')}
                  </Typography>
                  <FormControl>
                    <Select
                      id='extensionFileSelect'
                      value={value}
                      displayEmpty
                      renderValue={getFileExtensionLabel}
                      onChange={handleChangeFileExtension}
                      variant='standard'
                      disableUnderline
                      className={styles.select}
                      IconComponent={KeyboardArrowDownIcon}
                      classes={{
                        icon: styles.selectIcon,
                        select: styles.selectFocus
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        getContentAnchorEl: null,
                        MenuListProps: {
                          className: styles.selectMenuList
                        },
                        PaperProps: {
                          className: styles.selectMenuPaper
                        }
                      }}
                    >
                      <MenuItem
                        value='csv'
                        className={styles.menuItem}
                        classes={{
                          selected: styles.menuItemSelected
                        }}
                      >
                        {translate('csv')}
                      </MenuItem>
                      <MenuItem
                        value='xlsx'
                        className={styles.menuItem}
                        classes={{
                          selected: styles.menuItemSelected
                        }}
                      >
                        {translate('xlsx')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
                {/* <div> */}
                {/*  <FormControl component='fieldset'> */}
                {/*    <RadioGroup */}
                {/*      className={styles.radioGroup} */}
                {/*      aria-label='order' */}
                {/*      name='order' */}
                {/*      value={orderValue} */}
                {/*      onChange={handleOrderChange} */}
                {/*    > */}
                {/*      <FormControlLabel */}
                {/*        className={styles.labelRadio} */}
                {/*        value='0' */}
                {/*        control={<Radio inputRef={register()} className={styles.radioItem} />} */}
                {/*        label={translate('downloadDataForm.olderDataFirst')} */}
                {/*      /> */}
                {/*      <FormControlLabel */}
                {/*        className={styles.labelRadio} */}
                {/*        value='1' */}
                {/*        control={<Radio inputRef={register()} className={styles.radioItem} />} */}
                {/*        label={translate('downloadDataForm.latestDataFirst')} */}
                {/*      /> */}
                {/*    </RadioGroup> */}
                {/*  </FormControl> */}
                {/* </div> */}
                <div className={cx(styles.buttonWrapper, styles.downloadButtonWrapper)}>
                  {submitError && <div className={styles.noDataMessage}>{submitError}</div>}
                  <LoadingButton
                    type='submit'
                    fullWidth
                    loading={isSubmitting}
                    loadingPosition='end'
                    variant='contained'
                    className={styles.buttonSiguiente}
                  >
                    {translate('download')}
                  </LoadingButton>
                </div>
                <div className={styles.buttonWrapper}>
                  <Button
                    fullWidth
                    disabled={isSubmitting}
                    variant='contained'
                    className={styles.buttonCancelar}
                    onClick={onClickBack}
                  >
                    <ArrowBack className={styles.arrow} />
                    {translate('back')}
                  </Button>
                </div>
                <div className={styles.buttonWrapper}>
                  <Button
                    fullWidth
                    disabled={isSubmitting}
                    variant='contained'
                    className={styles.buttonCancelar}
                    onClick={onClickCancel}
                  >
                    {translate('cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </StylesProvider>
  )
}

DownloadSettingsForm.propTypes = {
  system: PropTypes.instanceOf(Map),
  translate: PropTypes.func.isRequired
}

export default withLocalize(DownloadSettingsForm)
